/* eslint-disable no-unused-vars */
/* eslint-disable react/prop-types */
import React, { useEffect, useRef, useState } from "react";
import CustomInput from "./CustomInput";
import CustomTextArea from "./CustomTextArea";
import { Button } from "@nextui-org/button";
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "@nextui-org/modal";
import AppConstant from "../assets/js/AppConstant";
import AppUtility from "../assets/js/AppUtility";
import { uploadFileService } from "../services/BlobService";
import { number } from "prop-types";
import AdjustAmountForm from "./AdjustAmountForm";
import {
  stringToTicketPhase,
  stringToTicketStatus,
  TicketPhase,
} from "../assets/js/AppTicketConstant";

export const NoteType = Object.freeze({
  Rejected: Symbol("rejected"),
  Else: Symbol("else"),
});

const NoteContainer = ({ note = null }) => {
  const lastestReview = note?.note?.subTicketReview;
  const subTicketDetail = note?.note?.subTicketDetail;

  let noteBackgroundColor;
  let noteHeader;
  if (note != null) {
    switch (note?.noteType) {
      case NoteType.Rejected:
        noteBackgroundColor = "bg-red-300 bg-opacity-35";
        noteHeader = "เหตุผลการปฏิเสธ";
        break;
      default:
        noteBackgroundColor = null;
        break;
    }
  }

  return (
    <>
      <div
        className={`${noteBackgroundColor} w-[43%] h-fit flex flex-col p-4 rounded-xl`}
      >
        <p className="font-bold text-lg">{noteHeader}</p>
        <div className="pt-8 pb-3">
          <AdjustAmountForm
            adjustAmount={lastestReview?.adjusted_amount}
            proposalAmount={
              stringToTicketPhase(lastestReview?.phase) == TicketPhase.Proposal
                ? subTicketDetail?.proposal_amount
                : subTicketDetail?.claim_amount
            }
            isDense
          />
        </div>
        <p className="text-lg break-words overflow-wrap-anywhere hyphens-auto">
          {lastestReview?.description}
        </p>
      </div>
    </>
  );
};

const EditTicketModal = ({
  note = null,
  initialValue = null,
  isOpen,
  onOpenChange,
  onConfirm,
}) => {
  const [formData, setFormData] = useState({
    sub_ticket_code: null,
    project_name: null,
    amount: null,
    description: null,
    attachment_file_urls: [],
  });

  const fileInputRef = useRef();
  const [selectFiles, setSelectFiles] = useState([]);
  const [errors, setErrors] = useState({});

  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    setFormData(initialValue ?? null);
    setSelectFiles([...(initialValue?.attachment_file_urls ?? [])]);
  }, [initialValue]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const newData = {
        ...prev,
        [name]: value,
      };
      setHasChanges(hasFormChanged(newData));
      return newData;
    });
  };

  const hasFormChanged = () => {
    if (!initialValue || !formData) return false;

    return (
      initialValue.amount !== formData.amount ||
      initialValue.description !== formData.description ||
      !arraysAreEqual(
        initialValue.attachment_file_urls,
        formData.attachment_file_urls
      )
    );
  };

  const arraysAreEqual = (arr1, arr2) => {
    if (arr1?.length !== arr2?.length) return false;
    return arr1?.every((item, index) => item === arr2[index]);
  };

  const removeFile = (fileToRemove) => {
    setSelectFiles((prevFiles) =>
      prevFiles.filter((file) => file !== fileToRemove)
    );

    setFormData((prevData) => ({
      ...prevData,
      attachment_file_urls: prevData.attachment_file_urls.filter(
        (file) => file !== fileToRemove
      ),
    }));
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];

    try {
      const uuid = AppUtility.uuidv4();
      const dateTimeIso = new Date(
        new Date().getTime() + 7 * 60 * 60 * 1000
      ).toISOString();
      const fileExtension = file.name.split(".").pop();
      const fileName = `${dateTimeIso}_${uuid}.${fileExtension}`;

      const uploadedUrl = await uploadFileService(file, file.type, fileName);
      setSelectFiles((data) => [...data, uploadedUrl]);
      setFormData((data) => ({
        ...data,
        attachment_file_urls: [...data.attachment_file_urls, uploadedUrl],
      }));
    } catch (e) {
      setSelectFiles((data) => data.filter((f) => f !== file));
    }
  };

  const validateForm = (data) => {
    const errors = {};

    if (!data.amount) {
      errors.amount = "กรุณาระบุจำนวนเงิน";
    }

    if (!data.description.trim()) {
      errors.description = "กรุณาเลือกลักษณะค่าใช้จ่าย";
    }

    if (!selectFiles.length) {
      errors.attachment_file_urls = "กรุณาแนบไฟล์เอกสาร";
    }

    return errors;
  };

  const submitUpdateTicket = async () => {
    const newErrors = validateForm(formData);
    setErrors(newErrors);

    if (Object.keys(newErrors).length === 0) {
      onConfirm(formData);
      onOpenChange(false);
    }
  };

  const handleModalClose = () => {
    setErrors({});
    onOpenChange(false);
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onOpenChange={onOpenChange}
        isDismissable={false}
        closeButton={<></>}
        className="min-h-[75%] max-h-[75%] py-6 min-w-[80%]"
      >
        <ModalContent>
          {(onClose) => (
            <>
              <ModalBody className="h-full overflow-auto">
                <div className="rounded-md bg-white">
                  <div className="flex flex-row divide-x-2 gap-x-3">
                    {note != null ? <NoteContainer note={note} /> : <></>}
                    <div className="flex flex-col gap-y-3 w-full pl-5">
                      <CustomInput
                        label={"เลขที่คำขอ"}
                        name="sub_ticket_code"
                        isDisabled
                        value={formData?.sub_ticket_code}
                        error={errors.sub_ticket_code}
                        errorMessage={errors.sub_ticket_code}
                      />

                      <CustomInput
                        label={"ชื่อค่าใช้จ่าย"}
                        name="project_name"
                        isDisabled
                        value={formData?.project_name}
                        error={errors.project_name}
                        errorMessage={errors.project_name}
                      />

                      <CustomInput
                        label={"จำนวนเงินทั้งหมดก่อน VAT"}
                        name="amount"
                        value={formData?.amount}
                        error={errors.amount}
                        errorMessage={errors.amount}
                        onChange={handleChange}
                        type="number"
                      />

                      <CustomTextArea
                        label={"รายละเอียด"}
                        lableOpacity={100}
                        name="description"
                        value={formData?.description}
                        error={errors.description}
                        errorMessage={errors.description}
                        onChange={handleChange}
                      />

                      <div className="flex flex-col gap-y-3">
                        <input
                          ref={fileInputRef}
                          type="file"
                          className="hidden"
                          onChange={handleFileChange}
                        />
                        <Button
                          className={`bg-light-gray-300 text-black w-fit px-4 text-md mt-5 ${
                            errors.attachment_file_urls &&
                            "border-2 border-red-500"
                          }`}
                          radius="sm"
                          startContent={
                            <div className="text-md text-black">+</div>
                          }
                          onPress={() => fileInputRef.current.click()}
                        >
                          เพิ่มไฟล์
                        </Button>
                        <div className="flex flex-row flex-wrap gap-x-4 pt-2 gap-y-4">
                          {selectFiles.map((file, index) => {
                            const fileType =
                              AppUtility.getFileTypeFromUrl(file);
                            return (
                              <React.Fragment key={index}>
                                <div className="relative w-fit">
                                  <button
                                    onClick={() => removeFile(file)}
                                    className="absolute -top-2 -right-2 bg-black text-white rounded-full w-6 h-6 pb-0.5 leading-none text-base z-10"
                                  >
                                    ×
                                  </button>

                                  {fileType == "image" ? (
                                    <img
                                      src={`${AppConstant.STOARGE_ACCOUNT}/ticket-files/${file}?${AppConstant.STORAGE_TOKEN}`}
                                      alt=""
                                      className="rounded-md h-fit w-20 object-cover"
                                    />
                                  ) : (
                                    <div className="h-20 w-20 rounded-xl shrink-0 border-light-gray-300 border-3 bg-white flex items-center justify-center px-2 text-center">
                                      <p className="line-clamp-4 text-xs">
                                        {file}
                                      </p>
                                    </div>
                                  )}
                                </div>
                              </React.Fragment>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </ModalBody>

              <ModalFooter>
                <div className="flex flex-row justify-end gap-x-4">
                  <Button
                    className="bg-blue-600 text-white font-medium w-fit text-md mt-5"
                    radius="sm"
                    onPress={submitUpdateTicket}
                    isDisabled={!hasFormChanged()}
                  >
                    ยืนยันแก้ไข
                  </Button>

                  <Button
                    className="bg-[#D4D4D4] text-black  w-fit text-md mt-5"
                    radius="sm"
                    onPress={handleModalClose}
                  >
                    ยกเลิก
                  </Button>
                </div>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    </>
  );
};

export default EditTicketModal;
